import { useState, useMemo, useEffect } from 'react';
import { computeN1 } from '../../utils/estimation';
import { RATIO_LABELS } from '../../utils/zoneRatios';
import { fmt, fmtCHF } from '../../utils/format';
import { useEstimationStore } from '../../stores/estimationStore';
import { useOrganizationStore } from '../../stores/organizationStore';
import { listOrganizations } from '../../api/organizations';
import { getAllCantons, getCantonName } from '../../utils/cantonData';
import { getCommunesByCantonCode } from '../../utils/communeData';
import type { GrandProjet } from '../../types/estimation';
import { useTranslation } from 'react-i18next';
import styles from './ProjectListPage.module.css';

interface ProjectFormData {
  id: string;
  name: string;
  type: string;
  communes: string[];
  organization_id: string | null;
  ha_total: number;
  ha_industriel: number;
  horizon_years: number;
  zone_overrides: Record<string, Record<string, number>>;
  notes: string | null;
  change_summary: string;
}

interface Props {
  project?: GrandProjet;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}

function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30);
}

function toFormData(p?: GrandProjet): ProjectFormData {
  return {
    id: p?.id ?? '',
    name: p?.name ?? '',
    type: p?.type ?? 'quartier',
    communes: p?.communes ?? [],
    organization_id: p?.organization_id ?? null,
    ha_total: p?.ha_total ?? 30,
    ha_industriel: p?.ha_industriel ?? 0,
    horizon_years: p?.horizon_years ?? 30,
    zone_overrides: p?.zone_overrides ?? {},
    notes: p?.notes ?? null,
    change_summary: '',
  };
}

/** Editable ratio keys shown in the expandable section */
const EDITABLE_RATIO_KEYS = [
  'logements_per_ha',
  'inhabitants_per_logement',
  'emplois_per_logement_mixed',
  'sbp_per_logement',
  'pct_activites_sbp',
  'infra_per_logement',
  'classes_per_logements_short',
  'creche_places_per_logements',
  'park_per_m2',
];

export function ProjectForm({ project, onSubmit, onCancel }: Props) {
  const { t: tCommon, i18n } = useTranslation('common');
  const isEdit = !!project;
  const [data, setData] = useState<ProjectFormData>(() => toFormData(project));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRatios, setShowRatios] = useState(false);

  // Canton / commune selection (for new projects)
  const [selectedCantonCode, setSelectedCantonCode] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');
  const [communeFilter, setCommuneFilter] = useState('');

  const { tenantBenchmarks } = useEstimationStore();

  // Load orgs for mapping organization_id (used in edit mode)
  const organizations = useOrganizationStore((s) => s.organizations);
  const setOrganizations = useOrganizationStore((s) => s.setOrganizations);

  useEffect(() => {
    if (organizations.length === 0) {
      listOrganizations('flat').then(setOrganizations).catch(() => {});
    }
  }, [organizations.length, setOrganizations]);

  // Pre-fill canton/commune from existing project data when editing
  useEffect(() => {
    if (isEdit && project && organizations.length > 0 && !selectedCantonCode) {
      const org = organizations.find((o) => o.id === project.organization_id);
      if (org) {
        // Find canton code from org hierarchy
        if (org.level === 'canton') {
          setSelectedCantonCode(org.code);
        } else if (org.level === 'commune') {
          const canton = organizations.find((o) => o.id === org.parent_id);
          if (canton) setSelectedCantonCode(canton.code);
        }
      }
    }
  }, [isEdit, project, organizations, selectedCantonCode]);

  // Commune list from cantonData (static) or from DB orgs
  const staticCommunes = getCommunesByCantonCode(selectedCantonCode);
  const dbCommunes = useMemo(() => {
    if (!selectedCantonCode) return [];
    // Find canton org in DB by code
    const cantonOrg = organizations.find(
      (o) => o.level === 'canton' && o.code === selectedCantonCode
    );
    if (!cantonOrg) return [];
    return organizations
      .filter((o) => o.level === 'commune' && o.parent_id === cantonOrg.id)
      .map((o) => o.name);
  }, [selectedCantonCode, organizations]);

  // Merge static + DB commune names (deduplicated, sorted)
  const availableCommunes = useMemo(() => {
    const merged = new Set([...staticCommunes, ...dbCommunes]);
    return [...merged].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [staticCommunes, dbCommunes]);

  // Filtered communes based on search input
  const filteredCommunes = useMemo(() => {
    if (!communeFilter.trim()) return availableCommunes;
    const q = communeFilter.toLowerCase();
    return availableCommunes.filter((name) => name.toLowerCase().includes(q));
  }, [availableCommunes, communeFilter]);

  // Find the organization_id for the selected canton (if it exists in DB)
  const cantonOrgId = useMemo(() => {
    if (!selectedCantonCode) return null;
    const cantonOrg = organizations.find(
      (o) => o.level === 'canton' && o.code === selectedCantonCode
    );
    return cantonOrg?.id ?? null;
  }, [selectedCantonCode, organizations]);

  // Find commune org ID if available
  const communeOrgId = useMemo(() => {
    if (!selectedCommune || !cantonOrgId) return null;
    const communeOrg = organizations.find(
      (o) => o.level === 'commune' && o.parent_id === cantonOrgId && o.name === selectedCommune
    );
    return communeOrg?.id ?? null;
  }, [selectedCommune, cantonOrgId, organizations]);

  // Auto-generate project ID for new projects
  const autoId = useMemo(() => {
    if (isEdit) return data.id;
    const parts = ['CH'];
    if (selectedCantonCode) parts.push(selectedCantonCode);
    if (selectedCommune) parts.push(slugify(selectedCommune));
    if (data.name.trim()) parts.push(slugify(data.name));
    return parts.join('-');
  }, [isEdit, selectedCantonCode, selectedCommune, data.name, data.id]);

  function set<K extends keyof ProjectFormData>(key: K, value: ProjectFormData[K]) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  function setZoneOverride(zone: string, key: string, value: number) {
    setData((prev) => ({
      ...prev,
      zone_overrides: {
        ...prev.zone_overrides,
        [zone]: { ...(prev.zone_overrides[zone] || {}), [key]: value },
      },
    }));
  }

  // Live preview: compute estimation from total area and ZI
  const preview = useMemo(() => {
    if (data.ha_total <= 0) return null;
    return computeN1(data.ha_total, data.ha_industriel, {}, tenantBenchmarks);
  }, [data.ha_total, data.ha_industriel, tenantBenchmarks]);

  const canSubmit = isEdit
    ? data.name.trim() && data.change_summary.trim()
    : data.name.trim() && selectedCantonCode && data.communes.length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      const c = preview;
      const orgId = communeOrgId ?? cantonOrgId ?? data.organization_id;
      await onSubmit({
        id: isEdit ? data.id : autoId,
        name: data.name,
        type: data.type,
        communes: data.communes,
        organization_id: orgId,
        ha_total: data.ha_total,
        ha_industriel: data.ha_industriel,
        horizon_years: data.horizon_years,
        horizon: data.horizon_years,
        zone_overrides: data.zone_overrides,
        logements: c?.logements ?? 0,
        emplois: c?.emplois ?? 0,
        sbp_logements: c?.sbp_logements ?? 0,
        sbp_activites: c?.sbp_act ?? 0,
        sbp_equipements: c?.sbp_eq ?? 0,
        sbp_total: c?.sbp_total ?? 0,
        espaces_publics_m2: c?.espaces_publics ?? null,
        notes: data.notes,
        ...(isEdit ? { change_summary: data.change_summary } : {}),
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save project';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const RATIO_DEFAULTS: Record<string, number> = {
    logements_per_ha: 65,
    inhabitants_per_logement: 2.3,
    emplois_per_logement_mixed: 0.80,
    sbp_per_logement: 100,
    pct_activites_sbp: 0.12,
    infra_per_logement: 67000,
    classes_per_logements_short: 0.015,
    creche_places_per_logements: 0.025,
    park_per_m2: 400,
  };

  const getDefault = (key: string): number => RATIO_DEFAULTS[key] ?? 0;

  const getOverride = (key: string): number | undefined =>
    data.zone_overrides['default']?.[key];

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Row 1: Project name */}
      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
          Nom du projet *
        </label>
        <input
          className={styles.input}
          placeholder="e.g. Quartier Praille-Acacias"
          value={data.name}
          onChange={(e) => set('name', e.target.value)}
          required
          autoFocus={!isEdit}
        />
      </div>

      {/* Row 2: Canton dropdown */}
      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
          Canton *
        </label>
        <select
          className={styles.input}
          value={selectedCantonCode}
          onChange={(e) => {
            setSelectedCantonCode(e.target.value);
            setSelectedCommune('');
            setCommuneFilter('');
            set('communes', []);
          }}
          disabled={isEdit}
        >
          <option value="">{tCommon('selectCanton')}</option>
          {getAllCantons().map((c) => (
            <option key={c.code} value={c.code}>
              {getCantonName(c.code, i18n.language)} ({c.code})
            </option>
          ))}
        </select>
      </div>

      {/* Row 3: Commune selection */}
      {selectedCantonCode && (
        <div>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
            Communes *
            {data.communes.length > 0 && (
              <span style={{ marginLeft: 8, color: 'var(--color-primary)', fontWeight: 600 }}>
                ({data.communes.length} selected)
              </span>
            )}
          </label>

          {availableCommunes.length > 0 ? (
            <>
              {/* Selected communes as removable chips */}
              {data.communes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {data.communes.map((name) => (
                    <span
                      key={name}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 8px 3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                        background: 'rgba(99, 102, 241, 0.12)', color: 'var(--color-primary)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                      }}
                    >
                      {name}
                      <button
                        type="button"
                        onClick={() => {
                          setData((prev) => ({ ...prev, communes: prev.communes.filter((n) => n !== name) }));
                          if (selectedCommune === name) setSelectedCommune('');
                        }}
                        style={{
                          background: 'none', border: 'none', color: 'var(--color-primary)',
                          cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px',
                        }}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search input */}
              <input
                className={styles.input}
                placeholder={`Rechercher parmi ${availableCommunes.length} communes\u2026`}
                value={communeFilter}
                onChange={(e) => setCommuneFilter(e.target.value)}
                style={{ marginBottom: 6 }}
              />

              {/* Filtered commune buttons */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                padding: 10,
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6,
                minHeight: 38,
                maxHeight: 200,
                overflowY: 'auto',
              }}>
                {filteredCommunes.length > 0 ? (
                  filteredCommunes.map((name) => {
                    const isSelected = data.communes.includes(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          setData((prev) => ({
                            ...prev,
                            communes: isSelected
                              ? prev.communes.filter((n) => n !== name)
                              : [...prev.communes, name],
                          }));
                          if (!isSelected && !selectedCommune) setSelectedCommune(name);
                          if (isSelected && selectedCommune === name) setSelectedCommune('');
                        }}
                        style={{
                          padding: '4px 12px',
                          borderRadius: 4,
                          border: isSelected
                            ? '1px solid var(--color-primary)'
                            : '1px solid rgba(255,255,255,0.12)',
                          background: isSelected
                            ? 'rgba(99, 102, 241, 0.12)'
                            : 'transparent',
                          color: isSelected ? 'var(--color-primary)' : 'var(--color-text)',
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: isSelected ? 600 : 400,
                          transition: 'all 0.15s',
                        }}
                      >
                        {name}
                      </button>
                    );
                  })
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', padding: '8px 4px' }}>
                    Aucune commune trouvée
                  </div>
                )}
              </div>
            </>
          ) : (
            <input
              className={styles.input}
              placeholder="Communes (comma-separated)"
              value={data.communes.join(', ')}
              onChange={(e) => {
                const communes = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                set('communes', communes);
                if (communes.length > 0 && !selectedCommune) {
                  setSelectedCommune(communes[0]!);
                }
              }}
            />
          )}
        </div>
      )}

      {/* Auto-generated project ID */}
      {!isEdit && (
        <div>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
            Identifiant projet (auto-généré)
          </label>
          <input
            className={styles.input}
            value={autoId}
            readOnly
            style={{ opacity: 0.7, fontFamily: 'monospace', fontSize: 12 }}
          />
        </div>
      )}

      {/* Edit mode: show existing ID */}
      {isEdit && (
        <div>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
            Identifiant projet
          </label>
          <input
            className={styles.input}
            value={data.id}
            readOnly
            disabled
            style={{ opacity: 0.5 }}
          />
        </div>
      )}

      {/* Row 4: Total area + ZI inputs */}
      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6, display: 'block' }}>
          Surface du périmètre (hectares)
        </label>
        <div className={styles.formRow}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 2 }}>
              Surface totale du projet
            </label>
            <input
              className={styles.input}
              type="number"
              step="0.1"
              min="0"
              placeholder="30"
              value={data.ha_total || ''}
              onChange={(e) => set('ha_total', parseFloat(e.target.value) || 0)}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 2 }}>
              Zone industrielle (ZI)
            </label>
            <input
              className={styles.input}
              type="number"
              step="0.1"
              min="0"
              max={data.ha_total}
              placeholder="0"
              value={data.ha_industriel || ''}
              onChange={(e) => set('ha_industriel', Math.min(parseFloat(e.target.value) || 0, data.ha_total))}
            />
          </div>
        </div>
        {data.ha_total > 0 && (
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6 }}>
            {tCommon('programmeMixte', 'Programme mixte')}: <strong>{Math.max(0, data.ha_total - data.ha_industriel).toFixed(1)} ha</strong>
          </div>
        )}
      </div>

      {/* Row 5: Horizon slider */}
      <div>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6, display: 'block' }}>
          {tCommon('horizonAnalyse', "Horizon d'analyse")}: <strong style={{ color: 'var(--color-primary)' }}>{data.horizon_years} ans</strong>
        </label>
        <input
          type="range"
          min={5}
          max={50}
          step={1}
          value={data.horizon_years}
          onChange={(e) => set('horizon_years', parseInt(e.target.value))}
          style={{ width: '100%', accentColor: 'var(--color-primary)' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-muted)' }}>
          <span>5 ans</span>
          <span>50 ans</span>
        </div>
      </div>

      {/* Row 6: Auto-calculated preview panel */}
      {preview && (
        <div style={{
          padding: 16,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(99, 102, 241, 0.02))',
          border: '1px solid rgba(99, 102, 241, 0.15)',
          borderRadius: 8,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Estimation automatique
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Logements</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>
                {fmt(preview.logements)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Habitants</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>
                {fmt(preview.inhabitants)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Emplois</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>
                {fmt(preview.emplois)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Coût total</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>
                {fmtCHF(preview.cost_total)}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>SBP total</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{fmt(preview.sbp_total)} m²</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Classes</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{preview.classes}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Crèches</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{preview.creches}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Densité</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{preview.density} log/ha</div>
            </div>
          </div>
        </div>
      )}

      {/* Row 7: Expandable ratios section */}
      <div>
        <button
          type="button"
          onClick={() => setShowRatios(!showRatios)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            fontSize: 12,
            padding: '4px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ transform: showRatios ? 'rotate(90deg)' : 'rotate(0)', transition: '0.2s', display: 'inline-block' }}>
            &#9654;
          </span>
          Ajuster les ratios
        </button>
        {showRatios && (
          <div style={{ marginTop: 8 }}>
            <div style={{ padding: 12, background: 'rgba(255,255,255,0.02)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.06)' }}>
              {EDITABLE_RATIO_KEYS.map((key) => {
                const defaultVal = getDefault(key);
                const override = getOverride(key);
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <label style={{ fontSize: 11, color: 'var(--color-text-muted)', flex: 1, minWidth: 0 }}>
                      {RATIO_LABELS[key] || key}
                    </label>
                    <input
                      className={styles.input}
                      type="number"
                      step="any"
                      placeholder={String(defaultVal)}
                      value={override ?? ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (Number.isNaN(val)) {
                          setData((prev) => {
                            const zo = { ...prev.zone_overrides };
                            if (zo['default']) {
                              const z = { ...zo['default'] };
                              delete z[key];
                              zo['default'] = z;
                            }
                            return { ...prev, zone_overrides: zo };
                          });
                        } else {
                          setZoneOverride('default', key, val);
                        }
                      }}
                      style={{ width: 90, textAlign: 'right' }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      <textarea
        className={styles.input}
        placeholder="Notes"
        value={data.notes ?? ''}
        onChange={(e) => set('notes', e.target.value || null)}
        rows={2}
        style={{ resize: 'vertical' }}
      />

      {/* Changelog — only shown when editing */}
      {isEdit && (
        <div>
          <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
            Changelog comment *
          </label>
          <input
            className={styles.input}
            placeholder="Describe what changed..."
            value={data.change_summary}
            onChange={(e) => {
              set('change_summary', e.target.value);
              setError('');
            }}
            required
            style={{ width: '100%' }}
          />
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--color-danger)', fontSize: 12 }}>{error}</div>
      )}

      <div className={styles.formActions}>
        <button type="button" className={styles.actionBtn} onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className={styles.createBtn} disabled={!canSubmit || submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
        </button>
      </div>
    </form>
  );
}
